import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  LoadingOverlay,
  Menu,
  Modal,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { notifyError, notifySuccess } from "../../utils/notify";
import {
  IconAlertCircle,
  IconCopy,
  IconDotsVertical,
  IconEye,
  IconPencil,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useFormTemplates } from "../../context/FormTemplatesContext";
import { useFeatureAccess } from "../../context/FeatureAccessContext";
import { isClinicalVertical } from "../../utils/verticalCapabilities";
import { FormCategory, FormTemplate } from "../../types/formTemplates";
import usePermissions from "../../hooks/usePermissions";
import { TemplateCard } from "../formTemplates/TemplateCard";
import { FormBuilder } from "../formTemplates/builder/FormBuilder";
import { TemplatePreview } from "../formTemplates/preview/TemplatePreview";
import {
  TemplateSortControl,
  usePersistedTemplateSort,
} from "../formTemplates/TemplateSortControl";
import { useSortedTemplates } from "../formTemplates/useSortedTemplates";
import primaryAction from "../common/primaryFilledButton.module.css";
import styles from "./FormTemplatesSettingsTab.module.css";

type LibraryTab = "standard" | "custom";

type BuilderState =
  | { mode: "closed" }
  | { mode: "create"; defaultCategory: FormCategory }
  | { mode: "create"; cloneFromSlug: string; seed: FormTemplate }
  | { mode: "edit"; template: FormTemplate };

/** "Create new template" options. We force the operator to pick the form's
 * SCOPE up front so the trap of "the modal silently defaulted to reusable"
 * can't happen — every new template carries an explicit intent. Order is
 * frequency-of-use (intake > waiver > clinical notes > closing). */
const CREATE_OPTIONS: ReadonlyArray<{
  category: FormCategory;
  label: string;
  description: string;
}> = [
  {
    category: "reusable",
    label: "Reusable Form",
    description: "Intake, HIPAA, anything the client signs once and reuses.",
  },
  {
    category: "per_visit",
    label: "Per-Visit Form",
    description: "Day-of consent or waiver. Re-signed every appointment.",
  },
  {
    category: "per_visit_clinical",
    label: "Clinical Notes (SOAP)",
    description: "Staff-written notes for one appointment. Never reused.",
  },
  {
    category: "post_appointment",
    label: "Post-Appointment Form",
    description:
      "Sent to the client after the appointment — attached to a post-appointment message.",
  },
];

const FormTemplatesSettingsTab: React.FC = () => {
  const {
    standardTemplates,
    customTemplates,
    isLoading,
    error,
    cloneTemplate,
    deleteTemplate,
    fetchTemplateById,
  } = useFormTemplates();

  const perms = usePermissions();
  const canManageTemplates =
    perms?.formsAndWaivers?.canManageTemplates !== false;

  // Clinical (SOAP) templates stay clinical-vertical only; hide the option elsewhere.
  const { businessVertical } = useFeatureAccess();
  const createOptions = useMemo(
    () =>
      isClinicalVertical(businessVertical)
        ? CREATE_OPTIONS
        : CREATE_OPTIONS.filter((o) => o.category !== "per_visit_clinical"),
    [businessVertical],
  );

  const [activeTab, setActiveTab] = useState<LibraryTab>("custom");
  const [builder, setBuilder] = useState<BuilderState>({ mode: "closed" });
  const [previewing, setPreviewing] = useState<FormTemplate | null>(null);
  const [cloningId, setCloningId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<FormTemplate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortBy, setSortBy] = usePersistedTemplateSort();

  const sortedStandard = useSortedTemplates(standardTemplates, sortBy);
  const sortedCustom = useSortedTemplates(customTemplates, sortBy);
  const templates = useMemo<FormTemplate[]>(
    () => (activeTab === "standard" ? sortedStandard : sortedCustom),
    [activeTab, sortedStandard, sortedCustom],
  );

  function handleTabChange(next: string | null) {
    if (next === "standard" || next === "custom") {
      setActiveTab(next);
    }
  }

  const handleCreateNew = (defaultCategory: FormCategory) => {
    setBuilder({ mode: "create", defaultCategory });
  };

  const handlePreview = async (template: FormTemplate) => {
    if (template.schema_json?.fields?.length) {
      setPreviewing(template);
      return;
    }
    try {
      const detail = await fetchTemplateById(template.slug || template.id);
      if (detail) setPreviewing(detail);
    } catch {
      notifyError("Failed to load template for preview.", {
        title: "Could not load template",
      });
    }
  };

  const handleEdit = async (template: FormTemplate) => {
    setEditingId(template.id);
    try {
      const detail = await fetchTemplateById(template.id);
      if (detail) setBuilder({ mode: "edit", template: detail });
    } catch {
      notifyError("Failed to load template for editing.", {
        title: "Could not load template",
      });
    } finally {
      setEditingId(null);
    }
  };

  const handleCopyToCustom = async (template: FormTemplate) => {
    const sourceSlug = template.slug || template.id;
    setCloningId(template.id);
    try {
      const created = await cloneTemplate({
        clone_from_slug: sourceSlug,
        name: `${template.name} (Copy)`,
        category: template.category,
      });
      setActiveTab("custom");
      notifySuccess(`${created.name} is ready to edit.`, {
        title: "Copied to your custom templates",
      });
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Unexpected error.", {
        title: "Could not copy template",
      });
    } finally {
      setCloningId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    setIsDeleting(true);
    try {
      await deleteTemplate(confirmDelete.id);
      notifySuccess(`Removed ${confirmDelete.name}.`, {
        title: "Template deleted",
      });
      setConfirmDelete(null);
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Unexpected error.", {
        title: "Could not delete template",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box p="md">
      <div className={styles.headerRow}>
        <Stack gap={2}>
          <Title order={3}>Form Templates</Title>
          <Text size="sm" c="dimmed">
            Manage standard and custom intake forms, waivers, and notes used
            across your client workflows.
          </Text>
        </Stack>
        {canManageTemplates && (
          <Menu shadow="md" position="bottom-end" withinPortal>
            <Menu.Target>
              <Button
                variant="filled"
                className={primaryAction.primaryFilled}
                leftSection={<IconPlus size={16} stroke={1.75} />}
              >
                Create New Template
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {createOptions.map((opt) => (
                <Menu.Item
                  key={opt.category}
                  onClick={() => handleCreateNew(opt.category)}
                >
                  <Stack gap={2}>
                    <Text size="sm" fw={600}>
                      {opt.label}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {opt.description}
                    </Text>
                  </Stack>
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        )}
      </div>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        classNames={{ root: styles.tabs, list: styles.tabsList }}
      >
        <Tabs.List>
          <Tabs.Tab value="custom">
            Custom Templates
            <Badge ml={8} size="xs" variant="default" color="gray" radius="sm">
              {customTemplates.length}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="standard">
            Standard Templates
            <Badge ml={8} size="xs" variant="default" color="gray" radius="sm">
              {standardTemplates.length}
            </Badge>
          </Tabs.Tab>
        </Tabs.List>
      </Tabs>

      <Group justify="flex-end" mt="sm" mb="md">
        <TemplateSortControl value={sortBy} onChange={setSortBy} />
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} mb="md">
          {error}
        </Alert>
      )}

      <Box pos="relative" mih={120}>
        <LoadingOverlay
          visible={editingId !== null}
          zIndex={5}
          overlayProps={{ blur: 1, opacity: 0.55, color: "#fff" }}
          loaderProps={{ size: "md", color: "var(--brand-teal)" }}
        />
        {isLoading && templates.length === 0 ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : templates.length === 0 ? (
          <div className={styles.empty}>
            <Text fw={600} size="sm">
              {activeTab === "custom"
                ? "No custom templates yet"
                : "No standard templates available"}
            </Text>
            <Text size="xs" c="dimmed">
              {activeTab === "custom"
                ? "Click Create New Template to build one, or copy a standard template to get started."
                : "Standard templates will appear here once published."}
            </Text>
          </div>
        ) : (
          <SimpleGrid
            cols={{ base: 1, sm: 2, lg: 3 }}
            spacing="md"
            className={styles.gridWrapper}
          >
            {templates.map((template) => (
              <TemplateCardWithMenu
                key={template.id}
                template={template}
                isCloning={cloningId === template.id}
                canManageTemplates={canManageTemplates}
                onPreview={() => handlePreview(template)}
                onEdit={() => handleEdit(template)}
                onCopy={() => handleCopyToCustom(template)}
                onDelete={() => setConfirmDelete(template)}
              />
            ))}
          </SimpleGrid>
        )}
      </Box>

      {builder.mode !== "closed" && (
        <FormBuilder
          mode={builder.mode}
          initialTemplate={
            builder.mode === "edit"
              ? builder.template
              : "seed" in builder
                ? builder.seed
                : undefined
          }
          cloneFromSlug={
            builder.mode === "create" && "cloneFromSlug" in builder
              ? builder.cloneFromSlug
              : undefined
          }
          defaultCategory={
            builder.mode === "create" && "defaultCategory" in builder
              ? builder.defaultCategory
              : undefined
          }
          onClose={() => setBuilder({ mode: "closed" })}
          onSaved={() => setBuilder({ mode: "closed" })}
        />
      )}

      <Modal
        opened={Boolean(previewing)}
        onClose={() => setPreviewing(null)}
        size="lg"
        centered
        title={
          <Text fw={700} size="lg">
            Preview
          </Text>
        }
      >
        {previewing && (
          <TemplatePreview
            name={previewing.name}
            description={previewing.description}
            schema={previewing.schema_json}
          />
        )}
      </Modal>

      <Modal
        opened={Boolean(confirmDelete)}
        onClose={() => (isDeleting ? undefined : setConfirmDelete(null))}
        title={
          <Text fw={700} size="lg">
            Delete template?
          </Text>
        }
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            This will permanently delete{" "}
            <Text span fw={600}>
              {confirmDelete?.name}
            </Text>
            . Forms already assigned to clients keep their data, but no new
            forms can be assigned from this template.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setConfirmDelete(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={isDeleting}
              onClick={handleConfirmDelete}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};

interface TemplateCardWithMenuProps {
  template: FormTemplate;
  isCloning: boolean;
  canManageTemplates: boolean;
  onPreview: () => void;
  onEdit: () => void;
  onCopy: () => void;
  onDelete: () => void;
}

const TemplateCardWithMenu: React.FC<TemplateCardWithMenuProps> = ({
  template,
  isCloning,
  canManageTemplates,
  onPreview,
  onEdit,
  onCopy,
  onDelete,
}) => {
  const isCustom = template.source === "custom";
  const isMock = Boolean(template.is_mock_placeholder);

  return (
    <Box style={{ position: "relative" }}>
      <TemplateCard template={template} onClick={onPreview} disabled={isMock} />
      <Box
        style={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Menu position="bottom-end" withinPortal shadow="md">
          <Menu.Target>
            <Button
              variant="subtle"
              color="gray"
              size="compact-sm"
              px={6}
              loading={isCloning}
              aria-label={`Actions for ${template.name}`}
            >
              <IconDotsVertical size={16} />
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconEye size={14} />}
              onClick={onPreview}
              disabled={isMock}
            >
              Preview
            </Menu.Item>
            {isCustom && canManageTemplates && (
              <>
                <Menu.Item
                  leftSection={<IconPencil size={14} />}
                  onClick={onEdit}
                >
                  Edit
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={14} />}
                  onClick={onDelete}
                >
                  Delete
                </Menu.Item>
              </>
            )}
            {!isCustom && canManageTemplates && (
              <Menu.Item
                leftSection={<IconCopy size={14} />}
                onClick={onCopy}
                disabled={isMock}
              >
                Copy to Custom
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      </Box>
    </Box>
  );
};

export default FormTemplatesSettingsTab;
